import { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";

// Login step state machine
const LOGIN_STEPS = {
  CREDENTIALS: "credentials",
  NEW_PASSWORD: "newPassword",
  MFA_SETUP: "mfaSetup",
  MFA_VERIFY: "mfaVerify",
  FORGOT_PASSWORD: "forgotPassword",
  RESET_PASSWORD: "resetPassword",
  MFA_RECOVERY_REQUEST: "mfaRecoveryRequest",
  MFA_RECOVERY_VERIFY: "mfaRecoveryVerify",
  MFA_RECOVERY_SUCCESS: "mfaRecoverySuccess",
};

// How long a recovery code stays valid, mirrored from the backend (10 min).
const MFA_RECOVERY_TTL_SECONDS = 600;

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  const [loginStep, setLoginStep] = useState(LOGIN_STEPS.CREDENTIALS);
  const [totpSecret, setTotpSecret] = useState(null);
  // MFA recovery: masked-email info line from the backend + code expiry timestamp
  const [mfaRecoveryInfo, setMfaRecoveryInfo] = useState("");
  const [mfaRecoveryExpiresAt, setMfaRecoveryExpiresAt] = useState(null);

  // Ref to hold the Cognito session string between challenge steps
  const sessionRef = useRef(null);
  // Email is needed for challenge and refresh calls
  const [email, setEmail] = useState(
    () => localStorage.getItem("authEmail") || ""
  );
  const listenersRef = useRef(new Set());

  /**
   * Refresh the auth token via the backend.
   */
  const refreshAuthToken = useCallback(async () => {
    const storedRefreshToken = localStorage.getItem("refreshToken");
    const storedEmail = localStorage.getItem("authEmail");
    if (!storedRefreshToken || !storedEmail) {
      throw new Error("No refresh credentials");
    }

    const { data } = await api.post("/auth/refresh", {
      email: storedEmail,
      refreshToken: storedRefreshToken,
    });

    return data.token;
  }, []);

  // Initialization: check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${JSON.parse(token)}`;

      api
        .get("/auth/me")
        .then(({ data }) => {
          setUser(data.user || data);
          setIsAuth(true);
          setLoading(false);
        })
        .catch(async () => {
          // Token might be expired, try refresh
          try {
            const newToken = await refreshAuthToken();
            localStorage.setItem("token", JSON.stringify(newToken));
            api.defaults.headers.Authorization = `Bearer ${newToken}`;

            const { data } = await api.get("/auth/me");
            setUser(data.user || data);
            setIsAuth(true);
          } catch {
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("authEmail");
            api.defaults.headers.Authorization = undefined;
          }
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [refreshAuthToken]);

  // Socket setup effect
  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      if (socket) {
        listenersRef.current.forEach((eventName) => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      const cognitoToken = localStorage.getItem("token");
      const socketInstance = socketConnection({
        user: {
          companyId: user.companyId,
          id: user.id,
          token: cognitoToken ? JSON.parse(cognitoToken) : null,
        },
      });

      if (socketInstance) {
        setSocket(socketInstance);

        setTimeout(() => {
          const eventName = `company-${user.companyId}-user`;

          const handleUserUpdate = (data) => {
            if (data.action === "update" && data.id === user.id) {
              setUser(data.user);
            }
          };

          if (socketInstance && typeof socketInstance.on === "function") {
            socketInstance.on(eventName, handleUserUpdate);
            listenersRef.current.add(eventName);
          }
        }, 100);
      }
    }

    return () => {
      if (socket && listenersRef.current.size > 0) {
        listenersRef.current.forEach((eventName) => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [user.id, user.companyId]);

  /**
   * Route a challenge response from the backend to the correct login step.
   */
  const handleChallengeResponse = useCallback((data) => {
    sessionRef.current = data.session;

    if (data.challengeName === "NEW_PASSWORD_REQUIRED") {
      setLoginStep(LOGIN_STEPS.NEW_PASSWORD);
    } else if (data.challengeName === "MFA_SETUP") {
      setTotpSecret(data.totpSecret);
      setLoginStep(LOGIN_STEPS.MFA_SETUP);
    } else if (data.challengeName === "SOFTWARE_TOKEN_MFA") {
      setLoginStep(LOGIN_STEPS.MFA_VERIFY);
    }
  }, []);

  /**
   * Complete post-login: store tokens, validate subscription, redirect.
   */
  const completeLogin = useCallback(
    async (token, refreshToken, userData, userEmail) => {
      localStorage.setItem("token", JSON.stringify(token));
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("authEmail", userEmail);
      api.defaults.headers.Authorization = `Bearer ${token}`;

      const { company } = userData;

      // Company settings logic
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );
        if (setting) {
          const signEnable = setting.value === "enable";
          if (setting.value === "enabled") {
            localStorage.setItem("sendSignMessage", signEnable);
          }
        }
      }

      localStorage.setItem("profileImage", userData.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = company.dueDate;
      }

      const vencimento = moment(dueDate).format("DD/MM/yyyy");
      const hojeInicio = moment().startOf("day");
      const vencimentoInicio = moment(dueDate).startOf("day");

      var diff = vencimentoInicio.diff(hojeInicio, "days");
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, "day");
      var dias = diff;

      if (before === true) {
        localStorage.setItem("companyDueDate", vencimento);
        setUser(userData);
        setIsAuth(true);
        setLoginStep(LOGIN_STEPS.CREDENTIALS);
        toast.success(i18n.t("auth.toasts.success"));

        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        history.push("/tickets");
      } else {
        setUser(userData);
        setIsAuth(true);
        setLoginStep(LOGIN_STEPS.CREDENTIALS);
        toast.warn(
          i18n.t("auth.toasts.expired") ||
            `Sua assinatura venceu em ${vencimento}. Renove para continuar usando o sistema.`
        );
        history.push("/subscription");
      }
    },
    [history]
  );

  /**
   * Step 1: Authenticate with email + password via backend.
   */
  const handleLogin = useCallback(
    async (userData) => {
      setLoading(true);

      try {
        const { data } = await api.post("/auth/login", {
          email: userData.email,
          password: userData.password,
        });

        if (data.type === "challenge") {
          setEmail(userData.email);
          localStorage.setItem("authEmail", userData.email);
          handleChallengeResponse(data);
          setLoading(false);
          return;
        }

        // type === "success"
        await completeLogin(
          data.token,
          data.refreshToken,
          data.user,
          userData.email
        );
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [completeLogin, handleChallengeResponse]
  );

  /**
   * Step 2 (conditional): Complete new password challenge.
   */
  const handleNewPassword = useCallback(
    async (newPassword) => {
      setLoading(true);

      try {
        const { data } = await api.post("/auth/challenge", {
          challengeName: "NEW_PASSWORD_REQUIRED",
          session: sessionRef.current,
          email,
          newPassword,
        });

        if (data.type === "challenge") {
          handleChallengeResponse(data);
          setLoading(false);
          return;
        }

        await completeLogin(data.token, data.refreshToken, data.user, email);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [email, completeLogin, handleChallengeResponse]
  );

  /**
   * Step 3 (first time): Verify TOTP setup with a code from authenticator app.
   */
  const handleMfaSetup = useCallback(
    async (verificationCode) => {
      setLoading(true);

      try {
        const { data } = await api.post("/auth/challenge", {
          challengeName: "MFA_SETUP",
          session: sessionRef.current,
          email,
          totpCode: verificationCode,
        });

        if (data.type === "challenge") {
          handleChallengeResponse(data);
          setLoading(false);
          return;
        }

        await completeLogin(data.token, data.refreshToken, data.user, email);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [email, completeLogin, handleChallengeResponse]
  );

  /**
   * Step 4 (every login): Verify TOTP code.
   */
  const handleMfaVerify = useCallback(
    async (totpCode) => {
      setLoading(true);

      try {
        const { data } = await api.post("/auth/challenge", {
          challengeName: "SOFTWARE_TOKEN_MFA",
          session: sessionRef.current,
          email,
          totpCode,
        });

        if (data.type === "challenge") {
          handleChallengeResponse(data);
          setLoading(false);
          return;
        }

        await completeLogin(data.token, data.refreshToken, data.user, email);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [email, completeLogin, handleChallengeResponse]
  );

  /**
   * Forgot password step 1: request a reset code via email.
   */
  const handleForgotPassword = useCallback(
    async (forgotEmail) => {
      setLoading(true);

      try {
        await api.post("/auth/forgot-password", { email: forgotEmail });
        setEmail(forgotEmail);
        localStorage.setItem("authEmail", forgotEmail);
        toast.success(i18n.t("login.toasts.resetCodeSent"));
        setLoginStep(LOGIN_STEPS.RESET_PASSWORD);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Forgot password step 2: submit the code + new password.
   */
  const handleResetPassword = useCallback(
    async (code, newPassword) => {
      setLoading(true);

      try {
        await api.post("/auth/confirm-forgot-password", {
          email,
          code,
          newPassword,
        });
        toast.success(i18n.t("login.toasts.passwordResetSuccess"));
        setLoginStep(LOGIN_STEPS.CREDENTIALS);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [email]
  );

  /**
   * Go back to the credentials step (escape from forgot-password flow).
   */
  const goBackToLogin = useCallback(() => {
    setLoginStep(LOGIN_STEPS.CREDENTIALS);
  }, []);

  const goToForgotPassword = useCallback(() => {
    setLoginStep(LOGIN_STEPS.FORGOT_PASSWORD);
  }, []);

  // ─── MFA recovery (lost authenticator device) ──────────────────────────────

  /**
   * Enter the MFA recovery flow from the MFA verify step.
   */
  const goToMfaRecovery = useCallback(() => {
    setMfaRecoveryInfo("");
    setMfaRecoveryExpiresAt(null);
    setLoginStep(LOGIN_STEPS.MFA_RECOVERY_REQUEST);
  }, []);

  /**
   * MFA recovery step 1: request a 6-digit code to the account email.
   */
  const handleRequestMfaRecovery = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/request_mfa_recovery", {
        username: email,
      });

      setMfaRecoveryInfo(data.info || "");
      setMfaRecoveryExpiresAt(Date.now() + MFA_RECOVERY_TTL_SECONDS * 1000);
      setLoginStep(LOGIN_STEPS.MFA_RECOVERY_VERIFY);
      toast.success(i18n.t("login.mfaRecovery.toasts.codeSent"));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [email]);

  /**
   * MFA recovery step 2: submit the code; on success the backend has reset
   * the MFA preference and the user must log in again from scratch.
   */
  const handleVerifyMfaRecovery = useCallback(
    async (code) => {
      setLoading(true);

      try {
        await api.post("/auth/verify_recovery_code", {
          username: email,
          code,
        });

        // Wipe any half-set auth state so the next login starts clean.
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        sessionRef.current = null;

        setLoginStep(LOGIN_STEPS.MFA_RECOVERY_SUCCESS);
        toast.success(i18n.t("login.mfaRecovery.toasts.resetSuccess"));
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [email]
  );

  /**
   * Leave the recovery flow and return to the credentials step.
   */
  const exitMfaRecovery = useCallback(() => {
    setMfaRecoveryInfo("");
    setMfaRecoveryExpiresAt(null);
    setLoginStep(LOGIN_STEPS.CREDENTIALS);
  }, []);

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Clean up socket before logout
      if (socket) {
        listenersRef.current.forEach((eventName) => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();

        if (typeof socket.disconnect === "function") {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout").catch(() => {});
    } catch (err) {
      // Ignore — we clear everything locally regardless
    } finally {
      setIsAuth(false);
      setUser({});
      setSocket(null);
      setLoginStep(LOGIN_STEPS.CREDENTIALS);
      setTotpSecret(null);
      setEmail("");
      setMfaRecoveryInfo("");
      setMfaRecoveryExpiresAt(null);
      sessionRef.current = null;
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("authEmail");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
    // MFA exports
    loginStep,
    totpSecret,
    handleNewPassword,
    handleMfaSetup,
    handleMfaVerify,
    handleForgotPassword,
    handleResetPassword,
    goBackToLogin,
    goToForgotPassword,
    // MFA recovery exports
    authEmail: email,
    mfaRecoveryInfo,
    mfaRecoveryExpiresAt,
    goToMfaRecovery,
    handleRequestMfaRecovery,
    handleVerifyMfaRecovery,
    exitMfaRecovery,
  };
};

export { LOGIN_STEPS };
export default useAuth;
